import { useReducer } from "react";
import type { WorkflowMetadataField, WorkflowMetadataGroup } from "../../shared/interfaces/Workflow";
import type { WorkflowV1 } from "../../shared/interfaces/WorkflowV1";
import type { WorkflowV2 } from "../../shared/interfaces/WorkflowV2";
import { findGroupedMeasurementByGroupId } from "./measurementOrdering";
import { generateWorkflowState } from "./state";
import type { GroupedMeasurement, GroupedPanel, WorkflowReducer } from "./types";
import { workflowReducer } from "./reducer";
import cloneDeep from "lodash/cloneDeep";

export function useWorkflowReducer(initialWorkflow: WorkflowV1 | WorkflowV2): WorkflowReducer {
  const [state, updateWorkflowDetails] = useReducer(workflowReducer, generateWorkflowState(initialWorkflow));

  const addNewMeasurementGroup = () => {
    updateWorkflowDetails({ type: "addNewMeasurementGroup" });
  };

  const removeMeasurementGroup = (groupId: string) => {
    updateWorkflowDetails({
      type: "updateAllGroupedMeasurements",
      groupedMeasurements: state.groupedMeasurements.filter(
        (groupedMeasurement) => groupedMeasurement.groupId !== groupId,
      ),
    });
  };

  const changeMeasurementGroupDetails = (updatedGroup: GroupedMeasurement) => {
    updateWorkflowDetails({
      type: "updateSingleGroupedMeasurement",
      updatedMeasurementGroup: updatedGroup,
    });
  };

  const addPanelsToGroup = (groupId: string, panels: GroupedPanel[]) => {
    const foundGroup = findGroupedMeasurementByGroupId(state.groupedMeasurements, groupId);

    if (!foundGroup) {
      return;
    }

    updateWorkflowDetails({
      type: "updateSingleGroupedMeasurement",
      updatedMeasurementGroup: {
        ...foundGroup,
        panels: [...foundGroup.panels, ...panels],
      },
    });
  };

  const removePanelFromGroup = (groupId: string, removePanel: GroupedPanel) => {
    const foundGroup = findGroupedMeasurementByGroupId(state.groupedMeasurements, groupId);

    if (!foundGroup) {
      return;
    }

    updateWorkflowDetails({
      type: "updateSingleGroupedMeasurement",
      updatedMeasurementGroup: {
        ...foundGroup,
        panels: foundGroup.panels.filter((panel) => panel.panelNo !== removePanel.panelNo),
      },
    });
  };

  const addCustomQuestionGroup = (customQuestionGroup?: WorkflowMetadataGroup) => {
    updateWorkflowDetails({
      type: "addCustomQuestionGroup",
      customQuestionGroup,
    });
  };

  const removeCustomQuestionGroup = (groupId: string) => {
    updateWorkflowDetails({
      type: "removeCustomQuestionGroup",
      groupId,
    });
  };

  const editCustomQuestionGroupName = (groupId: string, groupName: string) => {
    updateWorkflowDetails({
      type: "editCustomQuestionGroupName",
      groupId,
      groupName,
    });
  };

  const addCustomQuestionField = (groupId: string, fieldDetails: WorkflowMetadataField) => {
    updateWorkflowDetails({
      type: "addCustomQuestionField",
      groupId,
      fieldDetails,
    });
  };

  const editCustomQuestionField = (groupId: string, fieldDetails: WorkflowMetadataField) => {
    updateWorkflowDetails({
      type: "editCustomQuestionField",
      groupId,
      fieldDetails,
    });
  };

  const removeCustomQuestionField = (groupId: string, fieldId: string) => {
    updateWorkflowDetails({
      type: "removeCustomQuestionField",
      groupId,
      fieldId,
    });
  };

  const reorderOrderedMeasurements = (fromIndex: number, toIndex: number) => {
    let changedOrderedMeasurements = cloneDeep(state.orderedMeasurements);
    const [removed] = changedOrderedMeasurements.splice(fromIndex, 1);

    changedOrderedMeasurements.splice(toIndex, 0, removed);

    updateWorkflowDetails({
      type: "updateAllOrderedMeasurements",
      orderedMeasurements: changedOrderedMeasurements,
    });
  };

  const resetMeasurementsOrder = () => {
    updateWorkflowDetails({
      type: "resetMeasurementsOrder",
    });
  };

  return {
    state,
    updateWorkflowDetails,
    addNewMeasurementGroup,
    removeMeasurementGroup,
    changeMeasurementGroupDetails,
    addPanelsToGroup,
    removePanelFromGroup,
    addCustomQuestionGroup,
    removeCustomQuestionGroup,
    editCustomQuestionGroupName,
    addCustomQuestionField,
    editCustomQuestionField,
    removeCustomQuestionField,
    reorderOrderedMeasurements,
    resetMeasurementsOrder,
  };
}
